import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useOrder } from '../../context/ViewerOrderContext';
import reportData from '../../data/reportData.json';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Send as SendIcon,
  Info as InfoIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Lock as LockIcon,
  Lock
} from '@mui/icons-material';
import { Tabs, Tab, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Mic from '@mui/icons-material/Mic';
import MicOff from '@mui/icons-material/MicOff';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setContent, 
  setCursorPosition,
  placeCursor,
  appendTextToPlaceholder,
  moveToNextPlaceholder,
  undo,
  redo,
  replaceSelectedText
} from '../../store/editorSlice';
import { store } from '../../store/store';
import { debounce } from 'lodash';
import CustomSnackbar from '../CustomSnackbar';
import ReportPreviewButton from '../ReportPreviewButton';
import patientReports from '../../data/patientReports';

// Styled Components
const Container = styled.div`
  flex: 0.34;
  padding: 16px;
  position: relative;
  background: #000;
  color: #fff;
  height: 95vh;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(255, 255, 255, 0.1);
  margin-left: 16px;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h6`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const Paper = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  height: calc(100% - 250px);
  overflow-y: auto;
  box-shadow: none;
  border-radius: 8px;
  background: #000;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const ClinicalInfoSection = styled.div`
  padding: 12px;
  background: #1a1a1a;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h6`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 8px 0;
  text-transform: uppercase;
`;

const ClinicalDetails = styled.div`
  font-size: 13px;
  color: #ccc;
  margin-bottom: 16px;
  white-space: pre-wrap;
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DocumentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: #2a2a2a;
  border-radius: 4px;
  font-size: 13px;
`;

const DocumentName = styled.span`
  color: #fff;
`;

const DocumentDate = styled.span`
  color: #888;
  font-size: 12px;
`;

const PriorCasesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PriorCaseItem = styled.div`
  padding: 8px;
  background: #2a2a2a;
  border-radius: 4px;
  font-size: 13px;
`;

const StyledLockIcon = styled(Lock)`
  width: 4px;
  height: 4px;
  color: #fff;
`;

const ReportTextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 8px;
  background: #000;
  color: #fff;
  border: 1px solid #333;
  border-radius: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  overflow-y: auto;
  flex: 1;
  &:focus {
    outline: none;
    border-color: #4862F2;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Button = styled.button`
  border-radius: 23px;
  padding: 8px 16px;
  border: ${props => props.outlined ? '1px solid #fff' : 'none'};
  background: ${props => props.outlined ? 'none' : 'linear-gradient(107.93deg, #333 37.78%, #555 100%)'};
  color: #fff;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? '0.5' : '1'};
  font-size: 14px;
  outline: none;

  &:hover {
    background: linear-gradient(107.93deg, #111 37.78%, #333 70%);
    color: #fff;
  }
`;

const UploadButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UploadButton = styled.button`
  background: none;
  border: 1px solid #fff;
  color: #fff;
  padding: 4px 10px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: #fff;
    color: #000;
  }
`;

const MicButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const SelectedTemplateName = styled.div`
  font-size: 14px;
  color: #fff;
  font-family: 'Poppins', sans-serif;

  strong {
    color: #CF9FFF;
  }
`;

const PreviewButton = styled(Button)`
  background: none;
  border: none;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin: 0 8px;
  outline: none;

  &:disabled {
    color: #a9a9a9;
    cursor: not-allowed;
  }
`;

const TooltipIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: white;
  color: black;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  margin-right: 8px;
`;

const VoiceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
`;

const TabPanel = styled(Paper)`
  padding: 16px;
  background: white;
  border-radius: 8px;
  margin-top: 16px;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
`;

const TabContent = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
`;

const ReportEditor = ({ collapsed, studyId, orderId, displayOrderId, patientName, age, sex, clinicalDetails, clinicalDocs, priorCases, labUserId }) => {
  const navigate = useNavigate();
  const { orderData } = useOrder();
  const dispatch = useDispatch();
  const { content, cursorPosition, manualCursor } = useSelector(state => state.editor);
  const [listening, setListening] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [editorContent, setEditorContent] = useState('');
  const textareaRef = useRef(null);
  const dictationManagerRef = useRef(null);
  const listeningRef = useRef(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [messageSnackbar, setMessageSnackbar] = useState('');
  const [severitySnackbar, setSeveritySnackbar] = useState('success');
  const [reportPdfUrl, setReportPdfUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Mock patient data
  const patientData = {
    name: patientName || 'John Doe',
    age: age || '45',
    sex: sex || 'M',
    studyId: studyId || 'STUDY123',
    orderId: displayOrderId || 'ORDER456',
    labUserId: labUserId || 'LAB789'
  };

  useEffect(() => {
    if (textareaRef.current && cursorPosition !== null) {
      const textarea = textareaRef.current;
      const { selectionStart, selectionEnd } = textarea;
      if (selectionStart === selectionEnd) {
        textarea.setSelectionRange(cursorPosition, cursorPosition);
        textarea.focus();
        
        // Only scroll if cursor is near the bottom
        const lines = textarea.value.substr(0, cursorPosition).split('\n').length;
        const totalLines = textarea.value.split('\n').length;
        if (lines > totalLines - 5) {
          textarea.scrollTop = textarea.scrollHeight;
        }
      }
    }
  }, [cursorPosition]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return; // Early exit if ref is null

    if (textarea) {
      const handleSelectionChange = debounce(() => {
        const cursorPos = textarea.selectionStart;
        dispatch(setCursorPosition(cursorPos));
        dispatch(placeCursor(true));
      }, 100);

      textarea.addEventListener('select', handleSelectionChange);
      textarea.addEventListener('click', handleSelectionChange);
      textarea.addEventListener('keyup', handleSelectionChange);

      return () => {
        textarea.removeEventListener('select', handleSelectionChange);
        textarea.removeEventListener('click', handleSelectionChange);
        textarea.removeEventListener('keyup', handleSelectionChange);
        handleSelectionChange.cancel();
      };
    }
  }, [dispatch]);

  useEffect(() => {
    if (!content) {
      dispatch(setContent(editorContent));
    }
  }, [editorContent, dispatch, content]);

  // Get the raw_report from the current order data
  useEffect(() => {
    if (orderData?.data?.raw_report) {
      dispatch(setContent(orderData.data.raw_report));
    }
  }, [orderData, dispatch]);

  // Initialize dictation manager
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const Augnito = require('augnitosdk').Augnito;
      const manager = new Augnito({
        server: 'us.apis.augnito.ai',
        accountCode: '1dc72189-59d1-4f37-a1df-b07d519cb656',
        accessKey: '950f63eaf3bb44f29c66ebab0b60d3f5',
        userTag: '065d855f-4128-4f30-9e32-134137e0dce0',
        sourceApp: 'SDKdemo',
        lmId: '213803206',
      });

      manager.onFinalResult = (command) => {
        handleDictationResult(command.receivedText);
      };

      manager.onStateChanged = (isConnected) => {
        setListening(isConnected);
      };

      // Update the ref and state
      dictationManagerRef.current = manager;

      // Cleanup function using refs
      return () => {
        if (dictationManagerRef.current && listeningRef.current) {
          dictationManagerRef.current.toggleListening();
        }
        setListening(false);
      };
    }
  }, []);

  // Additional cleanup for navigation events
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (dictationManagerRef.current && listeningRef.current) {
        dictationManagerRef.current.toggleListening();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  function customTrim(str) {
    return str.replace(/^[ \t]+|[ \t]+$/g, '');
  }

  const handleDictationResult = (text) => {
    if (!text) return;
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    const processedText = customTrim(text);
    if (processedText.toLowerCase() === 'stop mic') {
      if (dictationManagerRef.current && listening) {
        dictationManagerRef.current.toggleListening();
        setListening(false);
      }
      return;
    }

    const nextKeyword = 'next';
    const segments = processedText.split(new RegExp(`(\\n{1,2}|${nextKeyword})`, 'i')).filter(segment => segment !== '');
    
    // Get the current selection range
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const hasSelection = selectionStart !== selectionEnd;
    
    // Initialize currentPosition based on selection or cursor
    let currentPosition = hasSelection ? selectionStart : (cursorPosition || textarea.selectionStart);
    
    // Process segments one by one
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      
      if (hasSelection && i === 0) {
        // Handle first segment with selection
        dispatch(replaceSelectedText({
          text: segment,
          selectionStart,
          selectionEnd
        }));
        currentPosition = selectionStart + segment.length;
        dispatch(setCursorPosition(currentPosition));
        textarea.setSelectionRange(currentPosition, currentPosition);
      } else {
        // For all other segments or when there's no selection
        if (/^\n{1,2}$/.test(segment)) {
          // Handle newlines
          dispatch(
            appendTextToPlaceholder({
              text: segment,
              cursorPosition: currentPosition,
            })
          );
        } else if (segment.toLowerCase() === nextKeyword.toLowerCase()) {
          // Handle next keyword
          if (!manualCursor) {
            dispatch(moveToNextPlaceholder());
          }
        } else {
          // Handle normal text
          dispatch(
            appendTextToPlaceholder({
              text: segment,
              cursorPosition: currentPosition,
            })
          );
        }
        const updatedState = store.getState().editor;
        currentPosition = updatedState.cursorPosition || currentPosition;
        textarea.setSelectionRange(currentPosition, currentPosition);
      }
      handleEditorChange(store.getState().editor.content);
    }
    
    // Final cursor position update
    requestAnimationFrame(() => {
      if (textarea) {
        textarea.focus();
        
        // Get final cursor position from Redux state
        const finalPosition = store.getState().editor.cursorPosition || currentPosition;
        
        // Set final cursor position
        textarea.setSelectionRange(finalPosition, finalPosition);
        
        // Handle scrolling if needed
        const lines = textarea.value.substr(0, finalPosition).split('\n').length;
        const totalLines = textarea.value.split('\n').length;
        if (lines > totalLines - 5) {
          textarea.scrollTop = textarea.scrollHeight;
        }
      }
    });
  };

  const handleMicClick = () => {
    if (dictationManagerRef.current) {
      if (listening) {
        dictationManagerRef.current.toggleListening();
        setListening(false);
      } else {
        dictationManagerRef.current.toggleListening();
        setListening(true);
        const contentState = store.getState().editor;
        const textarea = textareaRef.current;
        if (textarea) {
          const placeholderPattern = /\[\]/;
          const match = contentState.content.match(placeholderPattern);
          if (match && match.index !== undefined) {
            const firstPlaceholderIndex = match.index;
            textarea.focus();
            textarea.scrollTop = textarea.scrollHeight;
            textarea.setSelectionRange(firstPlaceholderIndex + 1, firstPlaceholderIndex + 1);
            dispatch(setCursorPosition(firstPlaceholderIndex + 1));
    } else {
            textarea.focus();
            textarea.scrollTop = textarea.scrollHeight;
          }
        }
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // const handleFullscreen = () => {
  //   if (!document.fullscreenElement) {
  //     document.documentElement.requestFullscreen();
  //     setIsFullscreen(true);
  //   } else {
  //     document.exitFullscreen();
  //     setIsFullscreen(false);
  //   }
  // };

  const handleBackToOrders = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    navigate('/dashboard');
  };

  const handleSubmit = () => {
    setOpenSnackbar(true);
    setMessageSnackbar('Report has been submitted successfully!');
    setSeveritySnackbar('success');
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  const handleCancel = () => {
    // Add cancel logic here
    console.log('Cancelling report');
  };

  const handlePreview = () => {
    if (content.trim()) {
      console.log('Study ID:', studyId);
      console.log('Patient Reports Mapping:', patientReports);
      
      // Get the report ID directly from the mapping
      const reportId = patientReports[studyId];
      console.log('Found Report ID:', reportId);
      
      if (reportId) {
        // Construct the PDF URL
        const pdfUrl = `/Report${reportId}.pdf`;
        console.log('PDF URL:', pdfUrl);
        setReportPdfUrl(pdfUrl);
        setIsPreviewOpen(true);
      } else {
        console.log('No report ID found for study:', studyId);
        setOpenSnackbar(true);
        setMessageSnackbar('No PDF found for this study');
        setSeveritySnackbar('error');
      }
    }
  };

  const handleCursorPositionChange = (e) => {
    const newPosition = e.target.selectionStart;
    dispatch(setCursorPosition(newPosition));
    dispatch(placeCursor(true));
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    dispatch(setContent(newContent));
    
    // Update cursor position after content change
    const newPosition = e.target.selectionStart;
    dispatch(setCursorPosition(newPosition));
    dispatch(placeCursor(true));
  };

  // Set cursor position when it changes in Redux
  useEffect(() => {
    if (textareaRef.current && cursorPosition !== null) {
      textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
      if (!manualCursor) {
        const lines = textareaRef.current.value.substr(0, cursorPosition).split('\n').length;
        const totalLines = textareaRef.current.value.split('\n').length;
        if (lines > totalLines - 5) {
          textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        }
      }
    }
  }, [cursorPosition, manualCursor]);

  // Update cursor position when content changes
  useEffect(() => {
    if (textareaRef.current && !manualCursor) {
      const placeholderPattern = /\[\]/;
      const match = content.match(placeholderPattern);
      if (match && match.index !== undefined) {
        const firstPlaceholderIndex = match.index;
        textareaRef.current.setSelectionRange(firstPlaceholderIndex + 1, firstPlaceholderIndex + 1);
        dispatch(setCursorPosition(firstPlaceholderIndex + 1));
      }
    }
  }, [content, manualCursor, dispatch]);

  const handleEditorChange = (newContent) => {
    setEditorContent(newContent);
    dispatch(setContent(newContent));
  };

  return (
    <Container collapsed={collapsed}>
      <Header>
        <Title>
          {/* <Lock style={{ fontSize: '12px' }} /> */}
          {patientData.name}, Age: {patientData.age}, Sex: {patientData.sex}
        </Title>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Tooltip title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
              <LockIcon style={{ fontSize: '12px' }} />
            </div>
          }>
            <Button 
              // onClick={handleFullscreen}
              style={{ 
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '40px'
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </Button>
          </Tooltip>
          <Button onClick={handleBackToOrders}>
            Back to Cases
          </Button>
        </div>
      </Header>

      <UploadButtonContainer>
        <UploadButton onClick={() => console.log('Change template clicked')}>
          Change Template &nbsp;
          <LockIcon style={{ fontSize: '12px' }} />
        </UploadButton>
        {selectedTemplate && (
          <SelectedTemplateName>
            Template: <strong>{selectedTemplateName}</strong>
          </SelectedTemplateName>
        )}
        <VoiceControls>
          <Tooltip
            title={
              <table style={{ borderCollapse: 'collapse', fontSize: '12px' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>Command</td>
                    <td style={{ padding: '2px 4px', fontWeight: 'bold' }}>Action</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>next line</td>
                    <td style={{ padding: '2px 4px' }}>Inserts a line break</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>next paragraph</td>
                    <td style={{ padding: '2px 4px' }}>Inserts a new paragraph</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>next</td>
                    <td style={{ padding: '2px 4px' }}>Moves to the next placeholder</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 4px' }}>stop mic</td>
                    <td style={{ padding: '2px 4px' }}>Turns off the mic</td>
                  </tr>
                </tbody>
              </table>
            }
            arrow
          >
            <TooltipIcon>
              ?
            </TooltipIcon>
          </Tooltip>
          <Tooltip title="Undo (Ctrl+Z)">
            <MicButton onClick={() => dispatch(undo())}>
              <UndoIcon fontSize="medium" />
            </MicButton>
          </Tooltip>
          <Tooltip title="Redo (Ctrl+Shift+Z or Ctrl+Y)">
            <MicButton onClick={() => dispatch(redo())}>
              <RedoIcon fontSize="medium" />
            </MicButton>
          </Tooltip>
          <MicButton onClick={handleMicClick}>
            {listening ? <MicIcon fontSize="medium" /> : <MicOffIcon fontSize="medium" />}
          </MicButton>
        </VoiceControls>
      </UploadButtonContainer>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontFamily: 'Poppins, sans-serif',
            minWidth: 100,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '0px',
            '&.Mui-disabled': {
              opacity: 0.5,
              cursor: 'not-allowed'
            }
          }
        }}
      >
        <Tab label="Report" />
        <Tab 
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <LockIcon style={{ fontSize: '12px' }} />
              Clinical Details
            </span>
          }
          disabled
        />
        <Tab 
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <LockIcon style={{ fontSize: '12px' }} />
              Chat
            </span>
          }
          disabled
        />
      </Tabs>

      <Paper>
        {activeTab === 0 && (
        <textarea
          key={`editor-${studyId}`} // Unique key based on studyId
          ref={textareaRef}
          value={content}
          onChange={(e) => {
            const newContent = e.target.value;
            // Only dispatch setContent if content actually changed
            if (newContent !== content) {
              dispatch(setContent(newContent));
              handleEditorChange(newContent);
            }
          }}
          placeholder="Enter Text"
          style={{
              height: 'calc(100% - 20px)',
              width: '100%',
              border: '1px solid #444',
            backgroundColor: '#000',
            color: '#fff',
            padding: '10px',
            borderRadius: '8px',
          }}
        />
        )}
        {activeTab === 1 && (
          <ClinicalInfoSection>
            <SectionTitle>Clinical Details</SectionTitle>
            <ClinicalDetails>{clinicalDetails || 'No clinical details available'}</ClinicalDetails>
            
            <SectionTitle>Clinical Documents</SectionTitle>
            <ClinicalDetails>{clinicalDocs || 'No clinical documents available'}</ClinicalDetails>

            <SectionTitle>Prior Cases</SectionTitle>
            <ClinicalDetails>{priorCases || 'No prior cases available'}</ClinicalDetails>
          </ClinicalInfoSection>
        )}
        {activeTab === 2 && (
          <ClinicalInfoSection>
            <SectionTitle>Chat</SectionTitle>
            <ClinicalDetails>Chat functionality coming soon...</ClinicalDetails>
          </ClinicalInfoSection>
        )}
      </Paper>

      <ButtonContainer>
        <Button 
        // onClick={handleCancel} 
        outlined>
          Cancel <LockIcon style={{ fontSize: '12px' }} />
        </Button>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button onClick={handlePreview} disabled={!content.trim()}>
            Preview
          </Button>
          {isPreviewOpen && reportPdfUrl && (
            <ReportPreviewButton 
              pdfUrl={reportPdfUrl} 
              initialOpen={true}
              onClose={() => {
                setIsPreviewOpen(false);
                setReportPdfUrl(null);
              }}
            />
          )}
        </div>
        <Button onClick={handleSubmit}>
          Submit
        </Button>
      </ButtonContainer>
      <CustomSnackbar
        open={openSnackbar}
        message={messageSnackbar}
        severity={severitySnackbar}
        onClose={() => setOpenSnackbar(false)}
      />
    </Container>
  );
};

export default ReportEditor; 